import dayjs, { type Dayjs } from 'dayjs'
import { birthDayOf } from './birthDay'

/**
 * When a notification is due, and how the Notifications page says it:
 * « Aujourd'hui », « Demain à 12:30 », « Mercredi 30 septembre »…
 */
export type DueSection = 'today' | 'tomorrow' | 'thisWeek' | 'earlier'

// Ordered so the most urgent bucket renders first; « Plus tôt » (news of the
// past days: accepted requests, sent messages) comes last.
export const DUE_SECTIONS: ReadonlyArray<DueSection> = ['today', 'tomorrow', 'thisWeek', 'earlier']

/** News of what happened (« X a accepté », « message parti »), not things to do. */
const NEWS_TYPES = new Set(['CONTACT_REQUEST_ANSWERED', 'SCHEDULED_MESSAGE_SENT'])

type Translate = (key: string, params?: Record<string, string | number>) => string

export interface DueItem {
  type: string
  createdAt: string
  eventAt?: string | null
  data?: unknown
}

const capitalize = (text: string) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text)

/** The day of a follow-up, the start of a rendez-vous, the sending of a scheduled message. */
export const dueDateOf = (item: DueItem): string => {
  const data = (item.data ?? {}) as {
    keepDate?: string
    startDateTime?: string
    scheduledAt?: string
    sentAt?: string
  }
  switch (item.type) {
    case 'SCHEDULED_MESSAGE_SENT':
      return data.sentAt ?? item.eventAt ?? item.createdAt
    case 'CONTACT_REQUEST_ANSWERED':
      return item.eventAt ?? item.createdAt
    case 'KEEP_CONTACT_REMINDER':
      return data.keepDate ?? item.eventAt ?? item.createdAt
    case 'CALENDAR_EVENT_UPCOMING':
      return data.startDateTime ?? item.eventAt ?? item.createdAt
    case 'SCHEDULED_MESSAGE_UPCOMING':
      return data.scheduledAt ?? item.eventAt ?? item.createdAt
    default:
      return item.createdAt
  }
}

/**
 * Requests and birthdays need attention today (tomorrow's birthdays, the
 * day before, go under « Demain »); news under the day they happened; the
 * rest by its date, late follow-ups with today's.
 */
export const dueSectionOf = (item: DueItem, now: Dayjs = dayjs()): DueSection => {
  if (item.type === 'CONTACT_REQUEST_CREATED') return 'today'
  if (item.type === 'BIRTHDAY_UPCOMING') {
    return (item.data as { eve?: boolean } | undefined)?.eve ? 'tomorrow' : 'today'
  }
  const due = dayjs(dueDateOf(item))
  if (NEWS_TYPES.has(item.type)) return due.isSame(now, 'day') ? 'today' : 'earlier'
  if (!due.isAfter(now, 'day')) return 'today'
  if (due.isSame(now.add(1, 'day'), 'day')) return 'tomorrow'
  return 'thisWeek'
}

/** Whole days a follow-up is late: 0 when it is due today or later. */
export const daysLate = (date: string, now: Dayjs = dayjs()): number =>
  Math.max(0, now.startOf('day').diff(dayjs(date).startOf('day'), 'day'))

/** Splits the list into Aujourd'hui / Demain / Cette semaine, keeping its order; empty buckets are dropped. */
export function groupByDueSection<T extends DueItem>(
  items: T[],
  now: Dayjs = dayjs(),
): { key: DueSection; data: T[] }[] {
  const buckets: Record<DueSection, T[]> = { today: [], tomorrow: [], thisWeek: [], earlier: [] }
  items.forEach((item) => buckets[dueSectionOf(item, now)].push(item))
  return DUE_SECTIONS.filter((key) => buckets[key].length > 0).map((key) => ({ key, data: buckets[key] }))
}

/** « Aujourd'hui », « Demain à 12:30 », « Mercredi 30 septembre à 9:00 »… */
export const dueLabel = (
  date: string,
  t: Translate,
  { withTime }: { withTime: boolean },
  now: Dayjs = dayjs(),
): string => {
  const due = dayjs(date)
  const time = due.format('HH:mm')
  if (!due.isAfter(now, 'day')) {
    return withTime ? t('notifications.when.todayAt', { time }) : t('notifications.when.today')
  }
  if (due.isSame(now.add(1, 'day'), 'day')) {
    return withTime ? t('notifications.when.tomorrowAt', { time }) : t('notifications.when.tomorrow')
  }
  const day = capitalize(due.format('dddd D MMMM'))
  return withTime ? t('notifications.when.dayAt', { day, time }) : day
}

export type PostponeChoice = 'tomorrow' | 'in3Days' | 'inAWeek' | 'inAMonth'

/** The new date of a postponed follow-up, counted from today, at its usual time of day. */
export const postponedDate = (current: string, choice: PostponeChoice, now: Dayjs = dayjs()): string => {
  const usual = dayjs(current)
  const today = now.startOf('day')
  const target =
    choice === 'tomorrow'
      ? today.add(1, 'day')
      : choice === 'in3Days'
        ? today.add(3, 'day')
        : choice === 'inAWeek'
          ? today.add(7, 'day')
          : today.add(1, 'month')
  return target.hour(usual.hour()).minute(usual.minute()).second(0).millisecond(0).toISOString()
}

/** The age someone turns this year, or null when the birth year is not believable. */
export const ageThisYear = (birthDate: string | undefined | null, now: Dayjs = dayjs()): number | null => {
  if (!birthDate) return null
  const born = dayjs(birthDayOf(birthDate), 'YYYY-MM-DD')
  if (!born.isValid() || born.year() < 1900) return null
  const age = now.year() - born.year()
  return age >= 1 && age <= 120 ? age : null
}
